import React, { useEffect, useState } from 'react';
import { Box, Stack, Button, IconButton, Typography, Tooltip } from '@mui/material';
import { EditRounded } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { reduxHelper } from '../../../config/reduxHelper';
import { useSubmitForm } from '../../../hooks/useSubmitForm';
import { useFormData } from '../../../hooks/useFormData';
import { useJsonData } from '../../../hooks/useJsonData';
import { inputTypes } from '../../formBuilder/inputTypes';
import CustomFormTextField from '../../../customComponents/formInputs/customFormTextField';
import CustomFormParagraphField from '../../../customComponents/formInputs/customFormParagraphField';
import CustomFormRadioFields from '../../../customComponents/formInputs/customFormRadioFields';
import CustomFormCheckboxFields from '../../../customComponents/formInputs/customFormCheckboxFields';
import CustomFormMenuFields from '../../../customComponents/formInputs/customFormMenuFields';

/**
 * FlexibleFormBlock - A replacement for CustomForm.
 * Merges legacy form logic with Site Builder editing capabilities.
 */
const FlexibleFormBlock = ({ block, isSiteBuilder }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { siteBuilderSelectedChild } = useSelector((state) => state.reducer);

    const [formData, setFormData] = useState(null);
    const formId = block.props?.form_id || block.form_id;
    const isSelected = isSiteBuilder && siteBuilderSelectedChild?.uid === block.uid;

    const getFormData = async () => {
        if (!formId) return;
        const result = await useFormData(formId);
        if (result.error === false) {
            const jsonResponse = await useJsonData(result?.formPath);
            if (jsonResponse?.error === false) {
                setFormData(jsonResponse?.data);
            }
        }
    };

    useEffect(() => {
        if (formId) {
            getFormData();
        }
    }, [formId]);

    const submitForm = async () => {
        if (isSiteBuilder) return;
        let formDataObject = {};
        formDataObject['template_id'] = formId;
        formDataObject['form_data'] = JSON.stringify(formData);
        const result = await useSubmitForm(formDataObject);
        if (result) {
            enqueueSnackbar('Form submitted successfully', { variant: 'success' });
        } else {
            enqueueSnackbar('Something went wrong', { variant: 'error' });
        }
    };

    const handleEditForm = (e) => {
        e.stopPropagation();
        dispatch({
            type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD,
            payload: { ...block, showEditor: true },
        });
        dispatch({ type: reduxHelper.UPDATE_BLOCK_PROPERTIES, payload: true });
    };

    const handleSelectBlock = (e) => {
        if (isSiteBuilder) {
            e.stopPropagation();
            if (siteBuilderSelectedChild?.uid !== block.uid) {
                dispatch({
                    type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD,
                    payload: { ...block },
                });
            }
        }
    };

    if (!formId) {
        return (
            <Box onClick={handleSelectBlock} sx={{ p: 4, border: '1px dashed grey', textAlign: 'center', cursor: isSiteBuilder ? 'pointer' : 'default' }}>
                <Typography variant="body2" color="textSecondary">No form selected. Click to set up.</Typography>
                {isSiteBuilder && (
                    <Button startIcon={<EditRounded />} onClick={handleEditForm} size="small" variant="outlined" sx={{ mt: 1 }}>
                        Choose Form
                    </Button>
                )}
            </Box>
        );
    }

    return (
        <Box
            onClick={handleSelectBlock}
            sx={{
                position: 'relative',
                outline: isSelected ? '1px solid #1890ff' : 'none',
                outlineOffset: '2px',
                borderRadius: '4px',
                p: 1,
                cursor: isSiteBuilder ? 'pointer' : 'default',
                '&:hover .form-edit-btn': { opacity: 1 },
                ...block.props?.sx
            }}
        >
            {isSiteBuilder && (
                <Tooltip title="Edit Form Inputs">
                    <IconButton
                        className="form-edit-btn"
                        onClick={handleEditForm}
                        sx={{
                            position: 'absolute',
                            top: -15,
                            right: -15,
                            bgcolor: 'white',
                            boxShadow: 2,
                            zIndex: 10,
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            '&:hover': { bgcolor: '#f5f5f5' }
                        }}
                        size="small"
                        color="primary"
                    >
                        <EditRounded fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}

            <Stack gap={3} minWidth={300}>
                {formData?.inputs?.map((i, index) => {
                    switch (i?.variant) {
                        case inputTypes.textField:
                            return <CustomFormTextField key={index} item={i} index={index} />;
                        case inputTypes.inputArea:
                            return <CustomFormParagraphField key={index} item={i} index={index} />;
                        case inputTypes.radioButton:
                            return <CustomFormRadioFields key={index} item={i} index={index} />;
                        case inputTypes.checkbox:
                            return <CustomFormCheckboxFields key={index} item={i} index={index} />;
                        case inputTypes.dropDown:
                            return <CustomFormMenuFields key={index} item={i} index={index} />;
                        default:
                            return null;
                    }
                })}

                <Button
                    variant="outlined"
                    color="info"
                    onClick={submitForm}
                    disabled={isSiteBuilder}
                >
                    {block.props?.buttonLabel || 'Submit'}
                </Button>
            </Stack>
        </Box>
    );
};

export default FlexibleFormBlock;
